"use strict";
// SPDX-FileCopyrightText: 2024 Red Bee Media Ltd <https://www.redbeemedia.com/>
//
// SPDX-License-Identifier: MIT
Object.defineProperty(exports, "__esModule", { value: true });
exports.AbstractBaseEngine = exports.getAudioKind = exports.getTextKind = exports.getDashAudioKind = exports.getDashTextKind = exports.getQualityLevelName = exports.HIDDEN_TEXT_TRACK_KINDS = exports.SUPPORTED_TEXT_TRACK_KINDS = exports.LIVE_EDGE_THRESHOLD = void 0;
const media_event_filter_1 = require("@eyevinn/media-event-filter");
const js_player_shared_1 = require("@ericssonbroadcastservices/js-player-shared");
const common_1 = require("../device/common");
const ContractRestrictionsGuardian_1 = require("../utils/ContractRestrictionsGuardian");
const interfaces_1 = require("./interfaces");
const NativeErrors_1 = require("./utils/NativeErrors");
function createTrack(track) {
    return {
        id: track.id,
        language: track.language || (0, js_player_shared_1.getLabel)(track.language),
        label: track.label,
        kind: track.kind,
        raw: track,
    };
}
// time, in seconds, from edge considered live edge
exports.LIVE_EDGE_THRESHOLD = 30;
exports.SUPPORTED_TEXT_TRACK_KINDS = [
    "captions",
    "subtitles",
    "descriptions",
    "forced", // NON-STANDARD only appears in Safari for Forced CC/Subtitles
];
exports.HIDDEN_TEXT_TRACK_KINDS = ["forced"];
function getQualityLevelName(qualityLevel) {
    var _a;
    const suffixes = {
        720: "HD",
        1080: "Full HD",
        1440: "2K",
        2160: "4K",
    };
    const suffix = suffixes[qualityLevel.height];
    const resolution = (_a = qualityLevel.height) === null || _a === void 0 ? void 0 : _a.toString();
    const mbps = (qualityLevel.bandwidth / 1000000).toFixed(1);
    const framerate = qualityLevel.framerate
        ? Math.round(qualityLevel.framerate)
        : undefined;
    let name = "";
    name += suffix ? `${resolution} ${suffix}` : resolution;
    name += ` @ ${mbps} mbps`;
    name += framerate && framerate > 30 ? `, ${Math.round(framerate)} fps` : "";
    return name;
}
exports.getQualityLevelName = getQualityLevelName;
// the naming of alternate tracks differs between Safari Native/hls.js and Shaka/Dash.js (hls/dash)
function getDashTextKind(kind) {
    if (kind === "captions")
        return "caption";
    if (kind === "subtitles")
        return "subtitle";
    if (kind === "forced")
        return "forced-subtitle";
    return kind;
}
exports.getDashTextKind = getDashTextKind;
function getDashAudioKind(kind) {
    return kind && ["description", "alternative"].includes(kind)
        ? "alternate"
        : kind;
}
exports.getDashAudioKind = getDashAudioKind;
// Roles will always include at least one string that will be 'subtitle'
// If it's an alternate track it will for example be '["subtitle", "caption"]'
// Forced: 'forced' will be present in hls and 'forced-subtitle' in dash
function getTextKind(roles) {
    if (roles.includes("caption"))
        return "captions";
    if (roles.includes("description"))
        return "descriptions";
    if (roles.includes("chapters"))
        return "chapters";
    if (roles.includes("metadata"))
        return "metadata";
    if (roles.includes("forced") || roles.includes("forced-subtitle"))
        return "forced";
    return "subtitles";
}
exports.getTextKind = getTextKind;
// audio roles will always include at least one string that will either be an empty string '""' or 'main'
// If it's an alternate track it will for example be '["main", "alternate"]'
function getAudioKind(roles) {
    if (roles.includes(""))
        return "";
    if (roles.includes("alternate"))
        return "alternative";
    if (roles.includes("alternative"))
        return "alternative";
    if (roles.includes("main-desc"))
        return "main-desc";
    if (roles.includes("description"))
        return "description";
    if (roles.includes("commentary"))
        return "commentary";
    return "main";
}
exports.getAudioKind = getAudioKind;
class AbstractBaseEngine extends js_player_shared_1.EmitterBaseClass {
    constructor(videoElement, instanceSettings) {
        var _a;
        super();
        this.isSubtitlesSetManually = false;
        this.isSubtitlesCueAlreadyUpdate = false;
        this.shouldManuallyUpdateSubtitlesCue = false;
        this.isSeekDisabled = false;
        this.state = js_player_shared_1.PlaybackState.IDLE;
        if (!videoElement) {
            throw "[AbstractBaseEngine] Invalid constructor";
        }
        this.instanceSettings = instanceSettings;
        this.videoElement = videoElement;
        this.guardian = new ContractRestrictionsGuardian_1.ContractRestrictionsGuardian((_a = instanceSettings.playResponse) === null || _a === void 0 ? void 0 : _a.contractRestrictions);
        this.droppedFrames = 0;
        this.supposedCurrentTime = 0;
        this.videoEventListeners = [];
        if (this.instanceSettings.initOptions.nativeSkin === true) {
            this.videoElement.setAttribute("controls", "controls");
        }
        else {
            this.videoElement.removeAttribute("controls");
        }
        this.videoElement.setAttribute("crossorigin", "anonymous");
        this.mediaEventFilter = this.setupMediaEventFilter(this.videoElement);
    }
    setupEventListeners() {
        this.addNativeVideoEvents();
        this.addSubtitleEvents();
        this.addAudioTrackEvents();
        this.suppressAutoSubtitles();
        this.on(interfaces_1.EngineEvents.ERROR, this.onEngineEventError.bind(this));
    }
    /**
     * When playing live streams converts the provided
     * player time to UTC, for VOD it returns -1
     * @param  {number} time
     * @return {number}
     */
    convertToUTCTime(time) {
        var _a;
        let startTime = (_a = this.utcStartTime) !== null && _a !== void 0 ? _a : this.fallbackUTCStartTime;
        if (Number.isFinite(startTime) === false && this.isLive()) {
            const duration = this.videoElement.seekable.length
                ? this.videoElement.seekable.end(0)
                : this.videoElement.duration;
            startTime = Date.now() - duration * 1000;
            this.fallbackUTCStartTime = startTime;
        }
        return startTime !== undefined && Number.isFinite(startTime)
            ? startTime + time * 1000
            : -1;
    }
    setUTCStartTime(startTime) {
        this.utcStartTime = startTime;
    }
    setPlaybackState(state) {
        this.state = state;
        this.emit(interfaces_1.EngineEvents.STATE_CHANGED, this.state);
    }
    // the parameters here is only used to provide the interface for the implementors
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    load(parameters) {
        this.setPlaybackState(js_player_shared_1.PlaybackState.LOADING);
        this.once(interfaces_1.EngineEvents.LOADED, () => {
            this.setPreferredLanguages({
                audio: parameters.audio,
                subtitle: parameters.subtitle,
            });
            this.onTracksChange();
        });
    }
    setPreferredLanguages({ audio, subtitle, }) {
        if (audio === null || audio === void 0 ? void 0 : audio.language) {
            const audioTracks = this.getAudioTracks();
            let audioTrack = audioTracks.find((track) => {
                if (audio.kind) {
                    return track.language === audio.language && track.kind === audio.kind;
                }
                return track.language === audio.language;
            });
            // If we cannot find an audio track with the correct language and kind, default to the first audio track.
            if (!audioTrack) {
                audioTrack = audioTracks[0];
            }
            this.setAudioTrack(audioTrack);
        }
        if (subtitle === null || subtitle === void 0 ? void 0 : subtitle.language) {
            const subtitleTrack = this.getSubtitleTracks().find((track) => {
                if (subtitle.kind) {
                    return (track.language === subtitle.language && track.kind === subtitle.kind);
                }
                return track.language === subtitle.language;
            });
            this.setSubtitleTrack(subtitleTrack);
        }
        else {
            this.setSubtitleTrack(undefined);
        }
    }
    setClientRestrictions(clientRestrictions) {
        this.guardian.setClientRestrictions(clientRestrictions);
    }
    play() {
        const promise = this.videoElement.play();
        return promise || Promise.resolve();
    }
    pause() {
        if (this.videoElement) {
            this.videoElement.pause();
        }
    }
    stop() {
        this.videoElement.pause();
        this.videoElement.removeAttribute("src");
        this.videoElement.load();
        this.setPlaybackState(js_player_shared_1.PlaybackState.IDLE);
    }
    getPlaybackState() {
        return this.state;
    }
    addNativeVideoEvents() {
        this.addOnVolumeChangeListener();
        this.addOnErrorListener();
    }
    setupMediaEventFilter(videoElement) {
        return (0, media_event_filter_1.getMediaEventFilter)({
            videoElement,
            allowResumeAfterEnded: true,
            callback: (event) => {
                switch (event) {
                    case media_event_filter_1.FilteredMediaEvent.LOADED:
                        this.onLoaded();
                        break;
                    case media_event_filter_1.FilteredMediaEvent.SEEKING:
                        this.onSeeking();
                        break;
                    case media_event_filter_1.FilteredMediaEvent.SEEKED:
                        this.onSeeked();
                        break;
                    case media_event_filter_1.FilteredMediaEvent.BUFFERING:
                        this.onBuffering();
                        break;
                    case media_event_filter_1.FilteredMediaEvent.BUFFERED:
                        this.onBuffered();
                        break;
                    case media_event_filter_1.FilteredMediaEvent.PLAY:
                        this.onPlay();
                        break;
                    case media_event_filter_1.FilteredMediaEvent.PLAYING:
                        this.onPlaying();
                        break;
                    case media_event_filter_1.FilteredMediaEvent.PAUSE:
                        this.onPause();
                        break;
                    case media_event_filter_1.FilteredMediaEvent.ENDED:
                        this.onEnded();
                        break;
                    case media_event_filter_1.FilteredMediaEvent.TIME_UPDATE:
                        this.onTimeUpdate();
                        break;
                    default:
                        break;
                }
            },
        });
    }
    addOnVolumeChangeListener() {
        this.addVideoEventListener(js_player_shared_1.HtmlVideoElementEvents.VOLUMECHANGE, this.onVolumeChange.bind(this));
    }
    addOnErrorListener() {
        this.addVideoEventListener(js_player_shared_1.HtmlVideoElementEvents.ERROR, this.onError.bind(this));
    }
    addOnPlaybackRateChangedListener() {
        this.addVideoEventListener(js_player_shared_1.HtmlVideoElementEvents.RATECHANGE, this.onPlaybackRateChanged.bind(this));
    }
    textTracksChangeHandler() {
        return this.onTextTracksChange.bind(this)(this.isSubtitlesSetManually);
    }
    addSubtitleEvents() {
        if (this.videoElement.textTracks) {
            this.videoElement.textTracks.addEventListener("change", (this.onTextTracksChangeReference =
                this.textTracksChangeHandler.bind(this)));
        }
    }
    addAudioTrackEvents() {
        // The HTMLVideoElement type doesn't have audioTracks on it even though it's in the spec 🙄
        // @ts-ignore
        if (this.videoElement.audioTracks) {
            // @ts-ignore
            this.videoElement.audioTracks.addEventListener("change", (this.onAudioTrackChangeReference = this.onAudioTrackChange.bind(this)));
        }
    }
    suppressAutoSubtitles() {
        if (!(0, common_1.isAppleSafari)())
            return;
        this.videoElement.addEventListener("play", () => {
            const textTracks = this.videoElement.textTracks
                ? Array.from(this.videoElement.textTracks)
                : [];
            for (const track of textTracks) {
                if (track.mode === "showing") {
                    track.mode = "disabled";
                }
            }
        });
    }
    isPlaying() {
        return this.state === js_player_shared_1.PlaybackState.PLAYING;
    }
    isLive() {
        return this.videoElement.duration === Infinity;
    }
    isAtLiveEdge() {
        if (this.isLive()) {
            const duration = this.getSeekable().end || this.getDuration();
            return this.getCurrentTime() > duration - exports.LIVE_EDGE_THRESHOLD;
        }
        return false;
    }
    isEnded() {
        return this.videoElement.ended;
    }
    onLoaded() {
        this.setPlaybackState(this.videoElement.paused ? js_player_shared_1.PlaybackState.PAUSED : js_player_shared_1.PlaybackState.PLAYING);
        this.emit(interfaces_1.EngineEvents.LOADED, undefined);
    }
    onPlaybackRateChanged() {
        this.emit(interfaces_1.EngineEvents.PLAYBACK_RATE_CHANGED, {
            playbackRate: this.videoElement.playbackRate,
        });
    }
    onBuffering() {
        this.emit(interfaces_1.EngineEvents.BUFFERING, undefined);
        this.setPlaybackState(js_player_shared_1.PlaybackState.BUFFERING);
    }
    onBuffered() {
        this.emit(interfaces_1.EngineEvents.BUFFERED, undefined);
        this.setPlaybackState(this.videoElement.paused ? js_player_shared_1.PlaybackState.PAUSED : js_player_shared_1.PlaybackState.PLAYING);
    }
    onPlay() {
        this.emit(interfaces_1.EngineEvents.PLAY, undefined);
    }
    onPause() {
        this.setPlaybackState(js_player_shared_1.PlaybackState.PAUSED);
        this.emit(interfaces_1.EngineEvents.PAUSE, undefined);
    }
    onPlaying() {
        this.setPlaybackState(js_player_shared_1.PlaybackState.PLAYING);
        this.emit(interfaces_1.EngineEvents.PLAYING, undefined);
    }
    onTimeUpdate() {
        this.pollDroppedFrames();
        this.supposedCurrentTime = this.videoElement.currentTime;
        this.guardian.isValidPosition(this.getCurrentTime(), true);
        this.emit(interfaces_1.EngineEvents.TIME_UPDATE, {
            currentTime: this.getCurrentTime(),
        });
    }
    pollDroppedFrames() {
        var _a;
        if (this.videoElement.getVideoPlaybackQuality) {
            const currentDroppedFrames = (_a = this.videoElement.getVideoPlaybackQuality()) === null || _a === void 0 ? void 0 : _a.droppedVideoFrames;
            if (currentDroppedFrames === this.droppedFrames)
                return;
            this.droppedFrames = currentDroppedFrames;
            this.emit(interfaces_1.EngineEvents.DROPPED_FRAMES, this.droppedFrames);
        }
    }
    onSeeking() {
        if (this.shouldManuallyUpdateSubtitlesCue) {
            this.isSubtitlesCueAlreadyUpdate = false;
        }
        const seekCheck = this.guardian.isValidPosition(this.getCurrentTime());
        if (seekCheck.valid) {
            this.setPlaybackState(js_player_shared_1.PlaybackState.SEEKING);
            this.emit(interfaces_1.EngineEvents.SEEKING, undefined);
        }
        else {
            this.seekTo(seekCheck.validPosition);
        }
    }
    onSeeked() {
        const seekCheck = this.guardian.isValidPosition(this.getCurrentTime());
        if (seekCheck.valid) {
            this.setPlaybackState(this.videoElement.paused ? js_player_shared_1.PlaybackState.PAUSED : js_player_shared_1.PlaybackState.PLAYING);
            this.emit(interfaces_1.EngineEvents.SEEKED, undefined);
        }
        if (!this.isSubtitlesCueAlreadyUpdate) {
            const track = this.getSubtitleTrack();
            for (let i = 0, len = this.videoElement.textTracks.length; i < len; i++) {
                const textTrack = this.videoElement.textTracks[i];
                if (track &&
                    textTrack.mode === "hidden" &&
                    exports.SUPPORTED_TEXT_TRACK_KINDS.includes(textTrack.kind)) {
                    const activeCues = (textTrack.activeCues ? Array.from(textTrack.activeCues) : []);
                    this.emit(interfaces_1.EngineEvents.SUBTITLE_CUE_CHANGED, activeCues);
                }
            }
        }
    }
    onVolumeChange() {
        this.emit(interfaces_1.EngineEvents.VOLUME_CHANGE, {
            volume: this.getVolume(),
            muted: this.getMuted(),
        });
    }
    onEnded() {
        this.supposedCurrentTime = 0;
        this.setPlaybackState(js_player_shared_1.PlaybackState.ENDED);
        this.emit(interfaces_1.EngineEvents.ENDED, undefined);
    }
    onError(evt) {
        if (evt) {
            const playerError = (0, NativeErrors_1.convertError)(evt);
            if (playerError) {
                this.emit(interfaces_1.EngineEvents.ERROR, playerError);
            }
        }
    }
    onEngineEventError() {
        this.setPlaybackState(js_player_shared_1.PlaybackState.ERROR);
    }
    onTextTracksChange(shouldUpdatePreferences = true) {
        this.shouldManuallyUpdateSubtitlesCue = true;
        const track = this.getSubtitleTrack();
        this.emit(interfaces_1.EngineEvents.SUBTITLE_CHANGED, {
            track,
            shouldUpdatePreferences,
        });
        this.emit(interfaces_1.EngineEvents.SUBTITLE_CUE_CHANGED, []);
        for (let i = 0, len = this.videoElement.textTracks.length; i < len; i++) {
            const textTrack = this.videoElement.textTracks[i];
            if (track &&
                textTrack.mode === "hidden" &&
                exports.SUPPORTED_TEXT_TRACK_KINDS.includes(textTrack.kind)) {
                //setTimeout to prevent cues from previously selected language from being taken as activeCues
                setTimeout(() => {
                    const activeCues = (textTrack.activeCues ? Array.from(textTrack.activeCues) : []);
                    this.emit(interfaces_1.EngineEvents.SUBTITLE_CUE_CHANGED, activeCues);
                }, 0);
                if (!textTrack.oncuechange) {
                    textTrack.oncuechange = () => {
                        this.isSubtitlesCueAlreadyUpdate = true;
                        const activeCues = (textTrack.activeCues ? Array.from(textTrack.activeCues) : []);
                        this.emit(interfaces_1.EngineEvents.SUBTITLE_CUE_CHANGED, activeCues);
                    };
                }
            }
            else {
                textTrack.oncuechange = null;
            }
        }
    }
    onAudioTrackChange() {
        this.emit(interfaces_1.EngineEvents.AUDIO_CHANGED, {
            track: this.getAudioTrack(),
        });
    }
    onTracksChange() {
        this.emit(interfaces_1.EngineEvents.TRACKS_CHANGED, {
            subtitleTrack: this.getSubtitleTrack(),
            audioTrack: this.getAudioTrack(),
            subtitleTracks: this.getSubtitleTracks(),
            audioTracks: this.getAudioTracks(),
        });
    }
    filterVisibleSubtitleTracks(tracks) {
        return tracks.filter((track) => track.kind && !exports.HIDDEN_TEXT_TRACK_KINDS.includes(track.kind));
    }
    setAutoPlay() {
        this.videoElement.setAttribute("autoplay", "true");
    }
    seekTo(pos) {
        this.videoElement.currentTime = pos;
    }
    setPlaybackRate(rate) {
        this.videoElement.playbackRate = rate;
    }
    getCurrentTime() {
        return this.videoElement.currentTime;
    }
    getUTCCurrentTime() {
        return this.convertToUTCTime(this.getCurrentTime());
    }
    getDuration() {
        const seekable = this.getSeekable();
        if (seekable === null || seekable === void 0 ? void 0 : seekable.end) {
            return seekable.end;
        }
        return this.videoElement.duration || 0;
    }
    getUTCDuration() {
        return this.convertToUTCTime(this.getDuration());
    }
    getSeekable() {
        const seekable = this.videoElement.seekable;
        if (seekable.length > 0) {
            return {
                start: seekable.start(0),
                end: seekable.end(0),
            };
        }
        return {
            start: 0,
            end: 0,
        };
    }
    getUTCSeekable() {
        const seekable = this.getSeekable();
        return {
            start: this.convertToUTCTime(seekable.start),
            end: this.convertToUTCTime(seekable.end),
        };
    }
    seekToLive() {
        const end = this.getSeekable().end;
        if (end) {
            this.videoElement.currentTime = end;
        }
    }
    setVolume(vol) {
        if (!vol || isNaN(vol)) {
            vol = 1;
        }
        this.videoElement.muted = false;
        this.videoElement.volume = vol;
    }
    getVolume() {
        return this.videoElement.volume;
    }
    getMuted() {
        return this.videoElement.muted;
    }
    toggleMuted() {
        this.setMuted(!this.videoElement.muted);
    }
    setMuted(muted) {
        this.videoElement.muted = muted;
    }
    getQualityLevel() {
        return js_player_shared_1.AUTO_QUALITY_LEVEL_DEFINITION;
    }
    getQualityLevels() {
        return [js_player_shared_1.AUTO_QUALITY_LEVEL_DEFINITION];
    }
    setAudioTrack(track) {
        // @ts-ignore
        const tracks = this.videoElement.audioTracks;
        if (tracks && tracks.length > 1) {
            let audioSet = false;
            // if multiple tracks for one language exist, only set one
            Array.from(tracks).forEach((t) => {
                if (!audioSet && t.id === track.id && t.kind === track.kind) {
                    t.enabled = true;
                    audioSet = true;
                }
                else {
                    t.enabled = false;
                }
            });
            if (!audioSet) {
                tracks[0].enabled = true;
            }
        }
    }
    getAudioTrack() {
        // @ts-ignore
        const tracks = this.videoElement.audioTracks
            ? // @ts-ignore
                Array.from(this.videoElement.audioTracks)
            : [];
        const activeTrack = tracks.find((track) => track.enabled);
        if (activeTrack) {
            return createTrack(activeTrack);
        }
    }
    getAudioTracks() {
        const internalTracks = 
        // @ts-ignore
        this.videoElement.audioTracks
            ? // @ts-ignore
                Array.from(this.videoElement.audioTracks)
            : [];
        return internalTracks
            .map((track) => createTrack(track))
            .filter((track, index, array) => array.findIndex((compTrack) => track.language === compTrack.language &&
            track.label === compTrack.label &&
            track.kind === compTrack.kind) === index);
    }
    setSubtitleTrack(track, setManually) {
        this.isSubtitlesSetManually = !!setManually;
        const tracks = this.videoElement.textTracks || [];
        // if multiple tracks for one language exist, only set one
        // to be able to set captions/subtitles with the same language we need to check the kind as well
        let subtitleSet = false;
        Array.from(tracks)
            .filter((t) => exports.SUPPORTED_TEXT_TRACK_KINDS.includes(t === null || t === void 0 ? void 0 : t.kind))
            .forEach((t) => {
            if (!subtitleSet &&
                t.language === (track === null || track === void 0 ? void 0 : track.language) &&
                t.kind === (track === null || track === void 0 ? void 0 : track.kind)) {
                t.mode = "hidden";
                subtitleSet = true;
            }
            else {
                t.mode = "disabled";
            }
            if (!track && t.kind === "forced") {
                t.mode = "hidden";
                subtitleSet = true;
            }
        });
    }
    /**
     * Helper methods for simpler cleanup of events
     */
    addVideoEventListener(type, listener) {
        if (this.videoElement) {
            this.videoEventListeners.push({
                type,
                listener,
            });
            this.videoElement.addEventListener(type, listener);
        }
    }
    removeVideoEventListener(type, listener) {
        var _a;
        const index = this.videoEventListeners.findIndex((handler) => handler.type === type && handler.listener === listener);
        this.videoEventListeners.splice(index, 1);
        (_a = this.videoElement) === null || _a === void 0 ? void 0 : _a.removeEventListener(type, listener);
    }
    removeVideoEventListeners() {
        if (this.videoElement) {
            this.videoEventListeners.forEach((evt) => {
                this.videoElement.removeEventListener(evt.type, evt.listener);
            });
            this.videoEventListeners = [];
        }
    }
    destroy() {
        this.removeVideoEventListeners();
        if (this.onTextTracksChangeReference) {
            this.videoElement.textTracks.removeEventListener("change", this.onTextTracksChangeReference);
        }
        if (this.onAudioTrackChangeReference) {
            // @ts-ignore
            this.videoElement.audioTracks.removeEventListener("change", this.onAudioTrackChangeReference);
        }
        if (this.mediaEventFilter) {
            this.mediaEventFilter.teardown();
        }
        this.stop();
        super.destroy();
    }
}
exports.AbstractBaseEngine = AbstractBaseEngine;
//# sourceMappingURL=AbstractBaseEngine.js.map